import { useState } from 'react';
import { useAuth } from '@/hooks/useAuth';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { MainLayout } from '@/components/layout/MainLayout';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { Skeleton } from '@/components/ui/skeleton';
import { SMSSettingsPanel } from '@/components/settings/SMSSettingsPanel';
import { TwoFactorSetup } from '@/components/settings/TwoFactorSetup';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { ScrollArea, ScrollBar } from '@/components/ui/scroll-area';
import { AccountTypeCard } from '@/components/settings/AccountTypeCard';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import { Loader2, RefreshCw } from 'lucide-react';

/** Response type from Calie_configureVoiceWebhook edge function */
interface WebhookConfigResponse {
  success: boolean;
  configured?: number;
  total?: number;
  errors?: string[];
  message?: string;
  count?: number;
  error?: string;
}

export function ProfilePage() {
  const { activeTenantId, memberships } = useAuth();
  const [isConfiguringWebhooks, setIsConfiguringWebhooks] = useState(false);

  const configureWebhooks = async () => {
    if (!activeTenantId) {
      toast.error('No active workspace selected');
      return;
    }

    setIsConfiguringWebhooks(true);
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) {
        toast.error('You must be logged in to configure webhooks');
        return;
      }

      const { data, error } = await supabase.functions.invoke<WebhookConfigResponse>('Calie_configureVoiceWebhook', {
        headers: {
          Authorization: `Bearer ${session.access_token}`,
        },
      });

      if (error) {
        console.error('[ProfilePage] Webhook configuration error:', error);
        toast.error(`Failed to configure webhooks: ${error.message}`);
        return;
      }

      console.log('[ProfilePage] Webhook configuration result:', data);
      
      if (data?.configured > 0) {
        toast.success(`Webhooks configured for ${data.configured} number(s)`);
      } else if (data?.errors?.length > 0) {
        toast.error(`Some webhooks failed: ${data.errors.join(', ')}`);
      } else {
        toast.info('No phone numbers found to configure');
      }
    } catch (err) {
      console.error('[ProfilePage] Webhook configuration exception:', err);
      toast.error('Failed to configure webhooks');
    } finally {
      setIsConfiguringWebhooks(false);
    }
  };

  const { data: workspace } = useQuery({
    queryKey: ['workspace', activeTenantId],
    queryFn: async () => {
      if (!activeTenantId) return null;
      const { data } = await supabase
        .from('calie_tenants')
        .select('name, created_at')
        .eq('tenant_id', activeTenantId)
        .maybeSingle();
      return data;
    },
    enabled: !!activeTenantId,
  });

  const { data: metrics, isLoading } = useQuery({
    queryKey: ['usage-metrics', activeTenantId],
    queryFn: async () => {
      if (!activeTenantId) return null;

      const [campaignsRes, adsRes, numbersRes, onboardingRes, tenantRes] = await Promise.all([
        supabase.from('calie_campaigns').select('campaign_id').eq('tenant_id', activeTenantId),
        supabase.from('calie_ads').select('id, campaign_id').eq('tenant_id', activeTenantId),
        supabase.from('calie_numbers').select('number_id').eq('tenant_id', activeTenantId),
        supabase.from('calie_onboarding_state').select('recording_enabled, twilio_connected_at').eq('tenant_id', activeTenantId).maybeSingle(),
        supabase.from('calie_tenants').select('twilio_sid').eq('tenant_id', activeTenantId).maybeSingle(),
      ]);

      const campaigns = campaignsRes.data || [];
      const ads = adsRes.data || [];
      const numbers = numbersRes.data || [];
      
      const adsByCampaign: Record<string, number> = {};
      ads.forEach(ad => {
        adsByCampaign[ad.campaign_id] = (adsByCampaign[ad.campaign_id] || 0) + 1;
      });
      const maxAdsPerCampaign = Math.max(0, ...Object.values(adsByCampaign));

      return {
        campaigns: campaigns.length,
        totalAds: ads.length,
        maxAdsPerCampaign,
        numbers: numbers.length,
        recordingEnabled: onboardingRes.data?.recording_enabled || false,
        twilioConnected: !!onboardingRes.data?.twilio_connected_at || !!tenantRes.data?.twilio_sid,
      };
    },
    enabled: !!activeTenantId,
  });


  const limits = {
    campaigns: 5,
    adsPerCampaign: 5,
    numbersPerAd: 1,
  };

  if (isLoading) {
    return (
      <MainLayout>
        <div className="space-y-4 sm:space-y-6">
          <div>
            <h1 className="text-xl sm:text-2xl font-bold">Profile & Plan</h1>
            <p className="text-sm text-muted-foreground">Manage your account and view plan details</p>
          </div>
          <div className="grid gap-4 sm:gap-6 md:grid-cols-2">
            <Skeleton className="h-32" />
            <Skeleton className="h-32" />
            <Skeleton className="h-48 md:col-span-2" />
          </div>
        </div>
      </MainLayout>
    );
  }

  return (
    <MainLayout>
      <div className="space-y-4 sm:space-y-6">
        <div>
          <h1 className="text-xl sm:text-2xl font-bold">Profile & Plan</h1>
          <p className="text-sm text-muted-foreground">Manage your account and view plan details</p>
        </div>

        <Tabs defaultValue="profile" className="w-full">
          <ScrollArea className="w-full">
            <TabsList className="inline-flex w-auto min-w-full sm:min-w-0">
              <TabsTrigger value="profile" className="min-h-[44px] px-4">Profile</TabsTrigger>
              <TabsTrigger value="security" className="min-h-[44px] px-4">Security</TabsTrigger>
              <TabsTrigger value="sms" className="min-h-[44px] px-4">SMS Settings</TabsTrigger>
            </TabsList>
            <ScrollBar orientation="horizontal" className="invisible" />
          </ScrollArea>

          <TabsContent value="profile" className="space-y-4 sm:space-y-6 mt-4 sm:mt-6">
            {/* Account Type Card - Full width on top */}
            <AccountTypeCard />
            
            <div className="grid gap-4 sm:gap-6 md:grid-cols-2">
              {/* Workspace Info */}
              <Card>
                <CardHeader className="p-4 sm:p-6">
                  <CardTitle className="text-base sm:text-lg">Workspace</CardTitle>
                  <CardDescription>Your active workspace details</CardDescription>
                </CardHeader>
                <CardContent className="p-4 pt-0 sm:p-6 sm:pt-0 space-y-4">
                  <div>
                    <p className="text-sm text-muted-foreground">Name</p>
                    <p className="font-medium">{workspace?.name || 'Loading...'}</p>
                  </div>
                  <div>
                    <p className="text-sm text-muted-foreground">Created</p>
                    <p className="text-sm">{workspace?.created_at ? new Date(workspace.created_at).toLocaleDateString() : '—'}</p>
                  </div>
                </CardContent>
              </Card>

              {/* Plan Info */}
              <Card>
                <CardHeader className="p-4 sm:p-6">
                  <CardTitle className="text-base sm:text-lg">Plan</CardTitle>
                  <CardDescription>Your current subscription details</CardDescription>
                </CardHeader>
                <CardContent className="p-4 pt-0 sm:p-6 sm:pt-0 space-y-4">
                  <div>
                    <p className="text-sm text-muted-foreground">Current Plan</p>
                    <div className="flex items-center gap-2">
                      <Badge variant="default">Starter</Badge>
                      <span className="text-sm text-muted-foreground">(Free)</span>
                    </div>
                  </div>
                  <div>
                    <p className="text-sm text-muted-foreground">Features</p>
                    <ul className="text-sm space-y-1">
                      <li className="flex items-center gap-2">
                        <span className={metrics?.twilioConnected ? "text-green-600" : "text-yellow-600"}>
                          {metrics?.twilioConnected ? "✓" : "⚠"}
                        </span>
                        Twilio Integration
                      </li>
                      <li className="flex items-center gap-2">
                        <span className={metrics?.recordingEnabled ? "text-green-600" : "text-muted-foreground"}>
                          {metrics?.recordingEnabled ? "✓" : "○"}
                        </span>
                        Call Recording
                      </li>
                      <li className="flex items-center gap-2">
                        <span className="text-green-600">✓</span>
                        Campaign Management
                      </li>
                    </ul>
                  </div>
                </CardContent>
              </Card>

              {/* Usage & Limits */}
              <Card className="md:col-span-2">
                <CardHeader className="p-4 sm:p-6">
                  <CardTitle className="text-base sm:text-lg">Usage & Limits</CardTitle>
                  <CardDescription>Current usage against your plan limits</CardDescription>
                </CardHeader>
                <CardContent className="p-4 pt-0 sm:p-6 sm:pt-0 space-y-6">
                  <div className="grid gap-4 sm:gap-6 sm:grid-cols-3">
                    <div className="space-y-2">
                      <div className="flex justify-between text-sm">
                        <span>Campaigns</span>
                        <span>{metrics?.campaigns || 0} / {limits.campaigns}</span>
                      </div>
                      <Progress value={(metrics?.campaigns || 0) / limits.campaigns * 100} />
                    </div>

                    <div className="space-y-2">
                      <div className="flex justify-between text-sm">
                        <span>Max Ads/Campaign</span>
                        <span>{metrics?.maxAdsPerCampaign || 0} / {limits.adsPerCampaign}</span>
                      </div>
                      <Progress value={(metrics?.maxAdsPerCampaign || 0) / limits.adsPerCampaign * 100} />
                    </div>

                    <div className="space-y-2">
                      <div className="flex justify-between text-sm">
                        <span>Phone Numbers</span>
                        <span>{metrics?.numbers || 0}</span>
                      </div>
                      <Progress value={Math.min((metrics?.numbers || 0) / 10 * 100, 100)} />
                      <p className="text-xs text-muted-foreground">Up to 1 number per ad</p>
                    </div>
                  </div>

                  <div className="pt-4 border-t">
                    <div className="flex items-center justify-between mb-3">
                      <h4 className="font-medium text-sm sm:text-base">Service Status</h4>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={configureWebhooks}
                        disabled={isConfiguringWebhooks || !metrics?.twilioConnected}
                        className="gap-2"
                      >
                        {isConfiguringWebhooks ? (
                          <Loader2 className="h-4 w-4 animate-spin" />
                        ) : (
                          <RefreshCw className="h-4 w-4" />
                        )}
                        Configure Webhooks
                      </Button>
                    </div>
                    <div className="grid gap-3 sm:grid-cols-2">
                      <div className="flex items-center justify-between p-3 border rounded-lg">
                        <span className="text-sm">Twilio Connection</span>
                        <Badge variant={metrics?.twilioConnected ? "default" : "secondary"}>
                          {metrics?.twilioConnected ? "Connected" : "Not Connected"}
                        </Badge>
                      </div>
                      <div className="flex items-center justify-between p-3 border rounded-lg">
                        <span className="text-sm">Call Recording</span>
                        <Badge variant={metrics?.recordingEnabled ? "default" : "secondary"}>
                          {metrics?.recordingEnabled ? "Enabled" : "Disabled"}
                        </Badge>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          <TabsContent value="security" className="space-y-4 sm:space-y-6 mt-4 sm:mt-6">
            <TwoFactorSetup />
          </TabsContent>

          <TabsContent value="sms" className="mt-4 sm:mt-6">
            <SMSSettingsPanel />
          </TabsContent>
        </Tabs>
      </div>
    </MainLayout>
  );
}
